"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "../src/context/AuthContext";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Building2,
  CalendarCheck2,
  CalendarDays,
  Download,
  FileSpreadsheet,
  Lock,
  Smartphone,
  Users,
  CheckCircle2,
  ShieldCheck,
  Zap
} from "lucide-react";
import { LandingHeader } from "../src/components/LandingHeader";
import { Button } from "../src/components/ui/Button";
import { usePwa } from "../src/pwa/PwaProvider";
import { useTheme } from "../src/context/ThemeContext";

const features = [
  {
    title: "Unified Dashboards",
    description: "Dedicated workspaces tailored for Directors, HR, and Employees with precise access controls.",
    icon: Users
  },
  {
    title: "Presence Management",
    description: "Automated workflows for time-off requests, approvals, and real-time balance tracking.",
    icon: CalendarDays
  },
  {
    title: "Cross-Platform Access",
    description: "Installable PWA experience that works seamlessly on both mobile and desktop environments.",
    icon: Smartphone
  },
  {
    title: "Secure Verification",
    description: "Cryptographically secure check-ins ensure organizational accountability and data integrity.",
    icon: ShieldCheck
  }
];

const howItWorks = [
  {
    title: "Check-in securely",
    description: "Employees mark attendance with verification controls designed for accuracy and accountability.",
    icon: CalendarCheck2
  },
  {
    title: "Request & approve leave",
    description: "Submit time-off requests, route approvals, and keep balances updated automatically.",
    icon: CalendarDays
  },
  {
    title: "Monitor and export",
    description: "HR and Directors review insights, audit activity, and export reports for compliance.",
    icon: Download
  }
];

const roleHighlights = [
  {
    title: "Directors",
    icon: Building2,
    bullets: [
      "Organization-wide visibility",
      "Team availability & leave trends",
      "Approval oversight and auditability",
      "Export-ready reporting"
    ]
  },
  {
    title: "HR Teams",
    icon: Users,
    bullets: [
      "Employee directory management",
      "Policy-aligned leave workflows",
      "Monthly exports (CSV/XLSX)",
      "Centralized attendance controls"
    ]
  },
  {
    title: "Employees",
    icon: CalendarCheck2,
    bullets: [
      "Fast daily attendance marking",
      "Self-serve leave requests",
      "Balance visibility",
      "Mobile-friendly PWA experience"
    ]
  }
];

const trustAndSecurity = [
  {
    title: "Role-based access",
    description: "Directors, HR, and Employees see only what they need — nothing more.",
    icon: Lock
  },
  {
    title: "Audit-ready actions",
    description: "Key workflow actions are traceable to support internal controls.",
    icon: ShieldCheck
  },
  {
    title: "Reliable verification",
    description: "Structured check-ins reduce errors and improve record integrity.",
    icon: CheckCircle2
  }
];

function roleDash(role) {
  if (role === "director") return "/director";
  if (role === "hr") return "/hr";
  if (role === "super_admin") return "/super-admin";
  return "/employee";
}

export default function HomePage() {
  const { user, loading } = useAuth();
  const { standalone } = usePwa();
  const router = useRouter();
  const { theme } = useTheme();
  const [activePerspective, setActivePerspective] = useState("director");

  const forceShowLanding = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("showLanding");
  }, []);

  const isPwaMode = useMemo(() => {
    if (typeof window === "undefined") return false;
    return Boolean(window.__IS_PWA) || window.location.search.includes("mode=pwa");
  }, []);

  // Ultra-fast PWA check to prevent any landing content from loading/flashing.
  // Keep it inside an effect so hooks remain unconditional (avoids prod-only crashes).
  useEffect(() => {
    if (!forceShowLanding && isPwaMode) {
      const isAdminLaunch =
        typeof window !== "undefined" &&
        (window.__PWA_APP === "admin" || window.localStorage.getItem("pwa_app") === "admin");
      router.replace(isAdminLaunch ? "/login?mode=pwa&app=admin" : "/login?mode=pwa");
    }
  }, [forceShowLanding, isPwaMode, router]);

  const perspectives = useMemo(
    () => [
      {
        id: "director",
        title: "For Directors",
        desc: "Strategic oversight and real-time organizational health data.",
        icon: Building2,
        imageDarkSrc: "/images/hr_dashboard.png",
        imageLightSrc: "/images/hr_light.png"
      },
      {
        id: "hr",
        title: "For HR Teams",
        desc: "Automated auditing, compliance, and time-off workflows.",
        icon: Users,
        imageDarkSrc: "/images/image_dark.png",
        imageLightSrc: "/images/image_light.png"
      },
      {
        id: "employee",
        title: "For Employees",
        desc: "Simple self-service tools for attendance and leave.",
        icon: CalendarCheck2,
        imageDarkSrc: "/images/employee.png",
        imageLightSrc: "/images/employe_light.png"
      }
    ],
    []
  );

  const activePreview = useMemo(
    () => {
      const base = perspectives.find((p) => p.id === activePerspective) ?? perspectives[0];
      const isDark = theme === "dark";
      return {
        ...base,
        imageSrc: isDark ? base.imageDarkSrc : base.imageLightSrc
      };
    },
    [activePerspective, perspectives, theme]
  );

  useEffect(() => {
    if (!loading) {
      if (user) {
        router.replace(roleDash(user.role));
      } else if (standalone) {
        // If app is opened as a PWA, skip landing page and go straight to login
        router.replace("/login");
      }
    }
  }, [user, loading, router, standalone]);

  // Guard: If we are already logged in, or if we are in a PWA, 
  // we show a loading screen while the useEffect handles the redirect.
  // This prevents the landing page content from "flashing".
  if (!forceShowLanding && (loading || user || standalone || isPwaMode)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-6">
          <div className="h-12 w-12 rounded-2xl bg-brand animate-pulse shadow-[0_0_40px_rgba(67,138,140,0.3)] flex items-center justify-center">
            <CalendarCheck2 className="text-white h-6 w-6" />
          </div>
          <p className="text-[10px] font-black text-brand uppercase tracking-[0.4em] animate-in-fade">
            {standalone || isPwaMode ? "Launching Application..." : "Verifying Identity..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="landing-page-root" className="flex min-h-screen flex-col bg-background text-foreground">
      <LandingHeader />

      <main className="flex-1">
        {/* ── Hero Section ── */}
        <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-32 overflow-hidden border-b border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-wider text-brand mb-8">
                <Zap size={14} /> System Status: Operational
              </div>
              
              <h1 className="text-4xl font-bold tracking-tight sm:text-6xl text-foreground mb-6 leading-[1.1]">
                Smart workforce management for 
                <span className="text-brand"> modern teams.</span>
              </h1>
              
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground font-medium">
                A professional-grade platform for attendance tracking, organizational records, and automated time-off workflows.
              </p>
              
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link href="/login">
                  <Button size="lg" className="h-12 px-8 rounded-xl w-full sm:w-auto font-bold">
                    Launch Application
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" size="lg" className="h-12 px-8 rounded-xl w-full sm:w-auto font-bold">
                    View Solutions
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Features Section ── */}
        <section id="features" className="py-24 bg-muted/20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <h2 className="text-2xl font-bold text-foreground">
                Core Capabilities
              </h2>
              <div className="mt-3 h-1 w-10 rounded-full bg-brand" />
            </div>
            
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {features.map(({ title, description, icon: Icon }) => (
                <div key={title} className="bg-background p-8 rounded-xl border border-border shadow-sm hover:border-brand/40 transition-all group">
                  <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-brand/5 text-brand mb-6 transition-transform group-hover:scale-105">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Perspectives Section ── */}
        <section className="py-24 border-t border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">
                  One platform.<br/>Tailored for everyone.
                </h2>
                <div className="space-y-6">
                  {perspectives.map((item) => {
                    const isActive = item.id === activePerspective;
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActivePerspective(item.id)}
                        className={[
                          "w-full text-left flex gap-4 rounded-2xl border p-4 transition-all",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          isActive
                            ? "border-brand/40 bg-brand/5 shadow-sm"
                            : "border-border bg-background hover:border-brand/30 hover:bg-muted/20"
                        ].join(" ")}
                        aria-pressed={isActive}
                      >
                        <div
                          className={[
                            "h-10 w-10 shrink-0 flex items-center justify-center rounded-lg transition-colors",
                            isActive ? "bg-brand text-white" : "bg-brand/5 text-brand"
                          ].join(" ")}
                        >
                          <Icon size={20} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-bold text-foreground">{item.title}</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-muted/30 p-3 shadow-sm">
                <div className="relative overflow-hidden rounded-xl border border-border bg-background">
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand/10 via-transparent to-transparent" />
                  <div className="relative w-full h-[240px] sm:h-[320px] lg:h-[420px] bg-muted/20 dark:bg-black/20">
                    <Image
                      key={activePreview.id}
                      src={activePreview.imageSrc}
                      alt={`${activePreview.title} dashboard preview`}
                      fill
                      quality={95}
                      unoptimized
                      priority={false}
                      // Use a realistic responsive hint so Next serves higher-res images on desktop.
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      className="object-contain object-center p-2 sm:p-3 dark:brightness-110 dark:contrast-110"
                    />
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 px-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {activePreview.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Click a role to preview
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── How It Works ── */}
        <section className="py-24 bg-muted/20 border-t border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <h2 className="text-2xl font-bold text-foreground">How it works</h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground font-medium">
                Simple flows for teams — built to reduce manual work and keep records accurate.
              </p>
              <div className="mt-4 h-1 w-10 rounded-full bg-brand" />
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {howItWorks.map(({ title, description, icon: Icon }) => (
                <div
                  key={title}
                  className="bg-background p-8 rounded-2xl border border-border shadow-sm hover:border-brand/40 transition-all"
                >
                  <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-brand/5 text-brand mb-6">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Role Highlights ── */}
        <section className="py-24 border-t border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <h2 className="text-2xl font-bold text-foreground">Built for every role</h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground font-medium">
                Clear responsibilities, clean workflows, and dashboards that match each persona.
              </p>
              <div className="mt-4 h-1 w-10 rounded-full bg-brand" />
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {roleHighlights.map(({ title, bullets, icon: Icon }) => (
                <div key={title} className="bg-background p-8 rounded-2xl border border-border shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-brand/5 text-brand">
                      <Icon size={20} />
                    </div>
                    <h3 className="text-base font-bold text-foreground">{title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <span className="mt-0.5 text-brand">
                          <CheckCircle2 size={16} />
                        </span>
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Trust & Security ── */}
        <section className="py-24 bg-muted/20 border-t border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center text-center mb-16">
              <h2 className="text-2xl font-bold text-foreground">Trust built-in</h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground font-medium">
                Designed for professional record-keeping with practical controls.
              </p>
              <div className="mt-4 h-1 w-10 rounded-full bg-brand" />
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {trustAndSecurity.map(({ title, description, icon: Icon }) => (
                <div
                  key={title}
                  className="bg-background p-8 rounded-2xl border border-border shadow-sm hover:border-brand/40 transition-all"
                >
                  <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-brand/5 text-brand mb-6">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Exports & Reporting ── */}
        <section className="py-24 border-t border-border/50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Exports & reporting</h2>
                <p className="mt-3 text-sm text-muted-foreground font-medium leading-relaxed max-w-xl">
                  Built for HR operations: download templates, bulk import employee data, and export attendance/leave records
                  for month-end processing.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border bg-muted/20 p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-brand/5 text-brand flex items-center justify-center">
                        <FileSpreadsheet size={18} />
                      </div>
                      <p className="text-sm font-bold text-foreground">CSV/XLSX friendly</p>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Export data in a format your workflow already supports.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/20 p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-brand/5 text-brand flex items-center justify-center">
                        <Download size={18} />
                      </div>
                      <p className="text-sm font-bold text-foreground">Download templates</p>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Reduce errors with guided imports and consistent formats.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-muted/20 p-8">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-2xl font-black text-foreground tracking-tight">3</p>
                    <p className="mt-1 text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
                      Roles supported
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground tracking-tight">PWA</p>
                    <p className="mt-1 text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
                      Mobile ready
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-foreground tracking-tight">1</p>
                    <p className="mt-1 text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
                      Unified platform
                    </p>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center">
                  <Link href="/login">
                    <Button size="lg" className="h-12 px-10 rounded-xl font-bold">
                      Explore in app
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA Panel ── */}
        <section className="py-24 bg-brand text-white overflow-hidden relative">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl font-bold sm:text-4xl mb-6">Ready to digitize your workspace?</h2>
            <p className="mb-10 text-lg text-white/80 font-medium max-w-2xl mx-auto">
              Implement a reliable, professional-grade protocol for your organization today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/login">
                <Button size="lg" className="bg-white text-brand hover:bg-white/90 h-12 px-10 rounded-xl font-bold shadow-xl">
                  Get Started
                </Button>
              </Link>
              <Link href="/connect-hr" className="text-white hover:text-white/80 font-bold text-sm uppercase tracking-wider flex items-center gap-2 px-6 py-2 transition-all">
                Contact HR Support <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-border bg-white dark:bg-card py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-[0.7rem] font-semibold text-muted-foreground uppercase tracking-widest">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-brand flex items-center justify-center text-white">
                <CalendarCheck2 size={16} />
              </div>
              <span className="text-foreground tracking-tight">Attendance</span>
            </div>
            
            <div className="flex items-center gap-10">
              <Link href="#" className="hover:text-brand transition-colors">Privacy</Link>
              <Link href="#" className="hover:text-brand transition-colors">Terms</Link>
              <Link href="#" className="hover:text-brand transition-colors">Security</Link>
            </div>
            
            <p className="tracking-tighter opacity-60">
              © {new Date().getFullYear()} Attendance App System.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}


